import React, { useCallback, useMemo } from "react";

import { useI18n } from "../../../../hooks/i18n-hooks";

import VStack from "../../../../ui_atoms/VStack";
import ScreenCard, { ButtonType } from "../../../../screens/ScreenCard";
import { BodyMediumText } from "../../../FormattedText";

import ActionButton from "../../../ActionButton";
import { useTheme } from "../../../../hooks/use-theme";
import Gap from "../../../../ui_atoms/Gap";
import Separator from "../../../../ui_atoms/separator";
import AutoSizeImage from "../../../AutoSizeImage";
import { useIntercom } from "../../../../hooks/intercom";
import { getPayFirstDaysSetAndLabel, getPayRemainingPercentsSetAndLabel } from "../helper";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../../../navigations/RootNavigation";
import { ClientProfile } from "../../../../__generated__/graphql";
import { useCurrentProfile } from "../../../../hooks/use-current-profile";

type IJMSelectionViewerProps = StackScreenProps<RootStackParamList, "IJMSelectionViewer">;

const IJMSelectionViewer: React.FC<IJMSelectionViewerProps> = ({ route }) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const currentProfile = useCurrentProfile<ClientProfile>();

    const { presentMessageComposer } = useIntercom();

    // Use ijmSelection from params if provided, otherwise use current client profile
    const { ijmSelection, cctLabel } = route.params;
    const ijmGroupName = ijmSelection?.ijmGroupName;
    const ijmPayFirstDays = ijmSelection?.ijmPayFirstDays;
    const ijmPayRemainingPercents = ijmSelection?.ijmPayRemainingPercents;

    const noIjm = ijmSelection?.ijmGroupId === null;

    const onValidate = useCallback(async () => {
        await presentMessageComposer();
    }, [presentMessageComposer]);

    const buttons = useMemo<ButtonType[]>(() => {
        return [
            {
                title: i18n.t("profile.contact_support"),
                onPress: onValidate,
                mode: "secondary",
            },
        ];
    }, [i18n, onValidate]);

    return (
        <ScreenCard buttons={buttons}>
            <VStack h="2/3">
                {!noIjm ? (
                    <>
                        <BodyMediumText>{i18n.t("ijm.viewer.root.text_1")}</BodyMediumText>
                        <Separator v={1 / 2} />

                        <VStack>
                            <BodyMediumText>
                                {i18n.t("ijm.viewer.group_select.title")}
                            </BodyMediumText>
                            <Gap gap={1 / 4} />
                            <ActionButton
                                text={ijmGroupName || i18n.t("ijm.ijm_group.place_holder")}
                                textColor={colors.greyAccessible}
                                textWidth="medium"
                                disabled
                            />
                            {currentProfile.client.useLegalEmployer.useLseCct ? (
                                <>
                                    <Gap gap={1} />
                                    <BodyMediumText>
                                        {i18n.t("ijm.viewer.waiting_select.title")}
                                    </BodyMediumText>
                                    <Gap gap={1 / 4} />
                                    <ActionButton
                                        text={i18n.t(
                                            getPayFirstDaysSetAndLabel(ijmPayFirstDays)[1],
                                        )}
                                        textColor={colors.greyAccessible}
                                        textWidth="medium"
                                        disabled
                                    />
                                </>
                            ) : null}
                            <Gap gap={1} />

                            {/* First days coverage */}
                            <BodyMediumText>
                                {i18n.t("ijm.viewer.cover_select.title")}
                            </BodyMediumText>
                            <Gap gap={1 / 4} />
                            <ActionButton
                                text={i18n.t(
                                    getPayRemainingPercentsSetAndLabel(ijmPayRemainingPercents)[1],
                                )}
                                textColor={colors.greyAccessible}
                                textWidth="medium"
                                numberOfLines={3}
                                disabled
                            />
                            <VStack alignment="center" style={{ opacity: 0.5 }}>
                                <Gap gap={3} />
                                <BodyMediumText>
                                    {i18n.t("ijm.editor.root.collaboration")}
                                </BodyMediumText>
                                <Gap gap={1 / 2} />
                                <AutoSizeImage
                                    source={require("../../../../assets/images/groupe_mutuel.png")}
                                    fixedHeight={40}
                                />
                            </VStack>
                        </VStack>
                    </>
                ) : (
                    <BodyMediumText>
                        {i18n.t("ijm.viewer.root.no-ijm", { cctLabel })}
                    </BodyMediumText>
                )}
            </VStack>
        </ScreenCard>
    );
};

export default IJMSelectionViewer;
